import { IS_ALFAJR } from '../constants/general';

const SEO_BASE_URL = IS_ALFAJR
  ? 'https://alfajr.qa'
  : 'https://ablemartonline.com/qatar';

const SEO_SITE_HOME = IS_ALFAJR
  ? 'https://alfajr.qa/'
  : 'https://ablemartonline.com/';

const SEO_ORDER_BASE = IS_ALFAJR
  ? 'https://alfajr.qa'
  : 'https://ablemartonline.com/qatar';

export { SEO_SITE_HOME, SEO_ORDER_BASE };

const DEFAULT_BRAND = IS_ALFAJR ? 'Alfajr' : 'AbleMart';

export const getProductImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === 'string') return img;
  return img.thumbnail || img.image || null;
};

export const getPrimaryProductCategory = (product = {}) => {
  if (Array.isArray(product.categories) && product.categories.length > 0) {
    const fromCategories = product.categories[0]?.category;
    if (fromCategories) {
      return String(fromCategories).trim();
    }
  }

  const { category } = product;
  if (Array.isArray(category)) {
    const first = category.find((value) => value != null && String(value).trim());
    return first ? String(first).trim() : '';
  }

  if (typeof category === 'string') {
    return category.trim();
  }

  return category != null ? String(category).trim() : '';
};

export const slugifyCategory = (category = '') =>
  encodeURIComponent(category.toLowerCase().replace(/\s+/g, ' ').trim());

export const slugifyUrlSlug = (slug = '') =>
  slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Category path segment for product URLs.
 * Prefer seoData.categorySlug when set (case-sensitive).
 * Else first category (lowercased via slugifyCategory).
 */
export const getProductUrlCategorySegment = (product = {}, categorySlugOverride = '') => {
  const fromSeo = String(categorySlugOverride || product?.seoData?.categorySlug || '').trim();
  if (fromSeo) return encodeURIComponent(fromSeo);
  return slugifyCategory(getPrimaryProductCategory(product) || 'category');
};

/**
 * Single canonical product URL — categorySlug (case-sensitive) or first category + hyphen slug.
 * Format: https://ablemartonline.com/qatar/order/{category}/{product-slug}/p/{id}
 */
export const buildSeoPreviewUrl = (product, urlSlug = '', categorySlug = '') => {
  const category = getProductUrlCategorySegment(product, categorySlug);
  const slug = slugifyUrlSlug(
    urlSlug
    || product?.seoData?.urlSlug
    || product?.name
    || product?.title
    || '',
  );
  const productId = product?.productId || '';
  if (!slug || !productId) return '';
  return `${SEO_BASE_URL}/order/${category}/${slug}/p/${productId}`;
};

/** Rewrite Product / Offer / Breadcrumb URLs in stored JSON-LD to the canonical link. */
export const rewriteSchemaProductUrls = (rawSchema, productUrl) => {
  if (!productUrl || !hasValue(rawSchema)) return normalizeSchemaField(rawSchema);
  try {
    const parsed = JSON.parse(extractJsonFromScriptTag(rawSchema));
    const rewrite = (node) => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) {
        node.forEach(rewrite);
        return;
      }
      if (typeof node.url === 'string' && node.url.includes('/p/')) {
        node.url = productUrl;
      }
      if (typeof node.item === 'string' && node.item.includes('/p/')) {
        node.item = productUrl;
      }
      Object.values(node).forEach(rewrite);
    };
    rewrite(parsed);
    return JSON.stringify(parsed);
  } catch {
    return normalizeSchemaField(rawSchema);
  }
};

export const getSeoStatusClass = (status = '', badgeClass = '') => {
  if (badgeClass) {
    if (badgeClass.includes('danger')) return 'seo-status-poor';
    if (badgeClass.includes('warning')) return 'seo-status-needs-improvement';
    if (badgeClass.includes('success')) return 'seo-status-good';
    if (badgeClass.includes('primary') || badgeClass.includes('info')) return 'seo-status-excellent';
  }
  const normalized = (status || '').toLowerCase();
  if (normalized === 'poor') return 'seo-status-poor';
  if (normalized.includes('needs')) return 'seo-status-needs-improvement';
  if (normalized === 'good') return 'seo-status-good';
  if (normalized === 'excellent') return 'seo-status-excellent';
  return 'seo-status-poor';
};

export const SEO_SCORE_THRESHOLDS = {
  EXCELLENT: 85,
  GOOD: 70,
  NEEDS_IMPROVEMENT: 50,
};

export const SEO_SCORE_FILTER_OPTIONS = [
  { value: 'all', label: 'All SEO Scores' },
  { value: 'excellent', label: 'Excellent (85+)' },
  { value: 'good', label: 'Good (70-84)' },
  { value: 'needs_improvement', label: 'Needs Improvement (50-69)' },
  { value: 'poor', label: 'Poor (Below 50)' },
];

export const getSeoStatusFromScore = (score) => {
  const value = Number(score) || 0;
  if (value >= SEO_SCORE_THRESHOLDS.EXCELLENT) return 'Excellent';
  if (value >= SEO_SCORE_THRESHOLDS.GOOD) return 'Good';
  if (value >= SEO_SCORE_THRESHOLDS.NEEDS_IMPROVEMENT) return 'Needs Improvement';
  return 'Poor';
};

const hasValue = (value) => Boolean(value && String(value).trim());

const hasSchemaMarkup = (value) => hasValue(value) && String(value).trim().length > 10;

export const extractJsonFromScriptTag = (text = '') => {
  const scriptRegex = /<script\s+type\s*=\s*['"]application\/ld\+json['"]>\s*([\s\S]*?)\s*<\/script>/i;
  const match = String(text).match(scriptRegex);
  if (match?.[1]) {
    return match[1].trim();
  }
  return String(text).trim();
};

const formatSchemaJson = (obj) => JSON.stringify(obj, null, 2);

const parseStoredSchema = (value) => {
  if (!hasValue(value)) return null;
  try {
    return JSON.parse(extractJsonFromScriptTag(value));
  } catch {
    return null;
  }
};

export const resolveSeoSchemaField = (storedValue, generator, canonicalUrl = '') => {
  if (hasSchemaMarkup(storedValue)) {
    const raw = canonicalUrl
      ? rewriteSchemaProductUrls(storedValue, canonicalUrl)
      : normalizeSchemaField(storedValue);
    const parsed = parseStoredSchema(raw);
    return parsed ? formatSchemaJson(parsed) : String(raw).trim();
  }
  return generator();
};

const normalizeSchemaField = (value) => {
  if (!hasValue(value)) return '';
  try {
    return JSON.stringify(JSON.parse(extractJsonFromScriptTag(value)));
  } catch {
    return String(value).trim();
  }
};

export const generateProductSchemaMarkup = (product) => {
  const seoData = product?.seoData || {};
  const productName = seoData.productTitle || product?.name || product?.title || '';
  const productDesc = seoData.productDescription || product?.description || '';
  const productUrl = buildSeoPreviewUrl(product);
  const images = mapProductImages(product?.images);
  const imageUrl = images.length > 0 ? getProductImageUrl(images[0]) : '';
  const productSku = product?.barcode || product?.sku || '';
  const productBrand = product?.brand || DEFAULT_BRAND;
  const productPrice = product?.displayRate ?? product?.rate ?? 0;
  const inStock = product?.isAvailable !== false;

  return formatSchemaJson({
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: productName,
    url: productUrl,
    image: imageUrl || '',
    description: productDesc,
    brand: {
      '@type': 'Brand',
      name: productBrand,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: product?.rating?.average?.toString() || '',
      reviewCount: product?.rating?.count?.toString() || '',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'QAR',
      price: String(productPrice),
      priceValidUntil: '',
      itemCondition: 'New',
      availability: inStock ? 'InStock' : 'OutOfStock',
    },
    ...(productSku ? { sku: productSku } : {}),
  });
};

export const generateDefaultBreadcrumbSchema = (product) => {
  const seoData = product?.seoData || {};
  const productCategory = getPrimaryProductCategory(product) || 'Default Category';
  const productName = seoData.productTitle || product?.name || product?.title || 'Product';
  const productUrl = buildSeoPreviewUrl(product);

  return formatSchemaJson({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SEO_SITE_HOME,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'All Categories',
        item: `${SEO_ORDER_BASE}/order`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: productCategory,
        item: `${SEO_ORDER_BASE}/order/${slugifyCategory(productCategory)}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: productName,
        item: productUrl,
      },
    ],
  });
};

export const generateDefaultFaqSchema = () =>
  formatSchemaJson({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [],
  });

const hasFaqContent = (faqSchema = '') => {
  if (!hasValue(faqSchema)) return false;
  try {
    const jsonStr = faqSchema
      .replace(/<script[^>]*>/gi, '')
      .replace(/<\/script>/gi, '')
      .trim();
    const parsed = JSON.parse(jsonStr);
    return Array.isArray(parsed?.mainEntity) && parsed.mainEntity.length > 0;
  } catch {
    return hasValue(faqSchema);
  }
};

export const calculateSeoScore = ({ seoData = {}, images = [] } = {}) => {
  const data = seoData || {};
  const imageList = images || [];

  const checks = [
    hasValue(data.productTitle),
    hasValue(data.metaTitle),
    hasValue(data.metaDescription),
    hasValue(data.urlSlug),
    hasValue(data.productDescription),
    hasValue(data.productSchemaMarkup),
    hasValue(data.breadCrumbSchema),
    hasFaqContent(data.faqSchema),
  ];

  if (imageList.length > 0) {
    const imagesWithAlt = imageList.filter((img) => hasValue(img?.altText)).length;
    checks.push(imagesWithAlt === imageList.length);
  }

  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
};

export const mapProductImages = (images = []) =>
  (images || []).map((img, index) => {
    if (typeof img === 'string') {
      return {
        order: String(index),
        image: img,
        thumbnail: img,
        altText: '',
      };
    }

    return {
      order: img.order != null ? String(img.order) : String(index),
      image: img.image || '',
      thumbnail: img.thumbnail || img.image || '',
      altText: img.altText || '',
    };
  });

export const initializeSeoFormData = (product) => {
  const seoData = product?.seoData || {};
  const productName = product?.name || product?.title || '';
  const formProduct = {
    ...product,
    seoData: {
      ...seoData,
      productTitle: seoData.productTitle || productName,
      productDescription: seoData.productDescription || product?.description || '',
    },
  };

  const canonicalUrl = buildSeoPreviewUrl(
    formProduct,
    seoData.urlSlug || '',
    seoData.categorySlug || '',
  );

  return {
    productTitle: seoData.productTitle || productName,
    metaTitle: seoData.metaTitle || product?.metaTitle || productName,
    metaDescription: seoData.metaDescription || product?.metaDescription || '',
    urlSlug: seoData.urlSlug || '',
    categorySlug: seoData.categorySlug || '',
    productDescription: seoData.productDescription || product?.description || '',
    images: mapProductImages(product?.images),
    productSchemaMarkup: resolveSeoSchemaField(
      seoData.productSchemaMarkup,
      () => generateProductSchemaMarkup(formProduct),
      canonicalUrl,
    ),
    breadCrumbSchema: resolveSeoSchemaField(
      seoData.breadCrumbSchema,
      () => generateDefaultBreadcrumbSchema(formProduct),
      canonicalUrl,
    ),
    faqSchema: resolveSeoSchemaField(
      seoData.faqSchema,
      () => generateDefaultFaqSchema(),
    ),
  };
};

export const buildSeoDataPayload = (formData, product = null) => {
  const productWithSeo = product
    ? {
        ...product,
        seoData: {
          ...(product.seoData || {}),
          urlSlug: formData.urlSlug || '',
          categorySlug: formData.categorySlug || '',
        },
      }
    : null;

  const canonicalUrl = productWithSeo
    ? buildSeoPreviewUrl(productWithSeo, formData.urlSlug || '', formData.categorySlug || '')
    : '';

  return {
    productTitle: formData.productTitle?.trim() || '',
    metaTitle: formData.metaTitle?.trim() || '',
    metaDescription: formData.metaDescription?.trim() || '',
    urlSlug: slugifyUrlSlug(formData.urlSlug || ''),
    // Case-sensitive; trim only
    categorySlug: (formData.categorySlug || '').trim(),
    productDescription: formData.productDescription?.trim() || '',
    productSchemaMarkup: canonicalUrl
      ? rewriteSchemaProductUrls(formData.productSchemaMarkup, canonicalUrl)
      : normalizeSchemaField(formData.productSchemaMarkup),
    breadCrumbSchema: canonicalUrl
      ? rewriteSchemaProductUrls(formData.breadCrumbSchema, canonicalUrl)
      : normalizeSchemaField(formData.breadCrumbSchema),
    faqSchema: normalizeSchemaField(formData.faqSchema),
  };
};

export const buildSeoSavePayload = (product, formData) => ({
  _id: product._id,
  productId: product.productId,
  seoData: buildSeoDataPayload(formData, product),
  images: (formData.images || []).map((img) => ({
    order: img.order != null ? String(img.order) : '0',
    image: img.image,
    thumbnail: img.thumbnail || img.image,
    altText: img.altText?.trim() || '',
  })),
});

export const getProductSeoScore = (product) => {
  if (typeof product?.seoScore === 'number') return product.seoScore;
  if (typeof product?.seoData?.seoScore === 'number') return product.seoData.seoScore;
  return calculateSeoScore({
    seoData: product?.seoData,
    images: mapProductImages(product?.images),
  });
};

export const getProductSeoStatus = (product) => {
  if (product?.seoStatus) return product.seoStatus;
  return getSeoStatusFromScore(getProductSeoScore(product));
};
